import { useEffect, useState } from "react";
import { ArrowUpRight, ChevronDown, Ruler, Wrench } from "lucide-react";
import { api, track } from "@/lib/api";
import { MerchantNote } from "./MerchantNote";
import { SmartImage } from "./SmartImage";

const CustomCta = ({ offer, label, placement, articleSlug, testId }) => (
  <a
    href={offer.tracking_url}
    target="_blank"
    rel="sponsored nofollow noopener"
    data-testid={testId}
    onClick={() =>
      track({
        event_type: "cta_click",
        cta_placement: placement,
        offer_category: "custom-saunas",
        article_slug: articleSlug,
        offer_id: offer.id,
        merchant: offer.merchant,
      })
    }
    className="inline-flex items-center gap-2 px-7 py-3.5 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold hover:bg-sage-dark transition-colors duration-300"
  >
    {label} <ArrowUpRight size={15} />
  </a>
);

export const CustomSaunaSection = ({ data, articleSlug }) => {
  const [offer, setOffer] = useState(null);
  const [openIdx, setOpenIdx] = useState(null);

  useEffect(() => {
    api
      .get("/offers/group", { params: { category: data.category } })
      .then((r) => setOffer((r.data.offers || [])[0] || null))
      .catch(() => {});
  }, [data.category]);

  return (
    <section id="custom-sauna" data-testid="custom-sauna-section" className="mt-16">
      <span className="eyebrow">{data.heading}</span>
      <p className="text-[17px] md:text-[18px] leading-[1.6] text-[#3F3F3F] mt-4 max-w-3xl">
        {data.intro}
      </p>

      <div className="grid sm:grid-cols-2 gap-px bg-line mt-7">
        {data.compare.map((c, i) => (
          <div
            key={c.label}
            data-testid={`sauna-compare-${i}`}
            className="bg-[#F4F2ED] px-6 py-6 border-t-[3px] border-t-sage"
          >
            <div className="flex items-center gap-2">
              {i === 0 ? (
                <Ruler size={15} className="text-sage-dark" />
              ) : (
                <Wrench size={15} className="text-sage-dark" />
              )}
              <span className="text-[11.5px] tracking-[0.16em] uppercase text-[#48563F] font-bold">
                {c.label}
              </span>
            </div>
            <p className="text-[16px] md:text-[17px] leading-[1.6] text-[#3F3F3F] mt-3">{c.note}</p>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line mt-px">
        {data.paths.map((p) => (
          <div key={p.label} data-testid={`sauna-path-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-white px-6 py-6">
            <h4 className="text-[16.5px] font-medium text-charcoal">{p.label}</h4>
            <p className="text-[15.5px] leading-[1.6] text-[#4A4A4A] mt-2">{p.note}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 border border-line bg-white grid lg:grid-cols-[1.25fr_1fr]">
        {data.why_image && (
          <div
            data-testid="custom-sauna-feature-image"
            className="relative h-[260px] sm:h-[360px] lg:h-auto lg:min-h-[460px] overflow-hidden"
          >
            <SmartImage
              src={data.why_image.image}
              alt={data.why_image.image_alt}
              sizes="(min-width:1024px) 55vw, 100vw"
              maxWidth={1280}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        )}
        <div className="px-6 md:px-9 py-8 md:py-10 flex flex-col justify-center">
          <span className="eyebrow">{data.why_heading}</span>
          <ul className="mt-4 space-y-2.5">
            {data.why.map((w, i) => (
              <li key={i} className="text-[16px] md:text-[17px] leading-[1.6] text-[#3F3F3F] pl-4 border-l-2 border-sage/40">
                {w}
              </li>
            ))}
          </ul>
          {offer && (
            <div className="mt-7">
              <CustomCta
                offer={offer}
                label={offer.cta_label}
                placement="CUSTOM SAUNA CTA"
                articleSlug={articleSlug}
                testId="custom-sauna-cta"
              />
              <MerchantNote merchant={offer.merchant} testId="custom-sauna-merchant" />
            </div>
          )}
        </div>
      </div>

      <div className="mt-10 bg-charcoal text-white px-6 md:px-9 py-9">
        <span className="text-[11.5px] tracking-[0.16em] uppercase text-[#C6D4BF] font-bold">
          {data.commercial_heading}
        </span>
        <p className="text-[16.5px] md:text-[17.5px] leading-[1.6] text-[#D7D4CF] mt-4 max-w-2xl">
          {data.commercial_note}
        </p>
        {offer && (
          <div className="mt-6">
            <CustomCta
              offer={offer}
              label={data.commercial_cta}
              placement="COMMERCIAL SAUNA CTA"
              articleSlug={articleSlug}
              testId="commercial-sauna-cta"
            />
            <MerchantNote merchant={offer.merchant} dark testId="commercial-sauna-merchant" />
          </div>
        )}
      </div>

      <div className="mt-12">
        <span className="eyebrow">{data.considerations_heading}</span>
        <div className="mt-5 border-t border-line">
          {data.considerations.map((c, i) => (
            <div key={c.label} className="border-b border-line">
              <button
                type="button"
                data-testid={`sauna-consider-toggle-${i}`}
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 text-left py-4"
              >
                <span className="text-[16.5px] md:text-[17.5px] text-charcoal font-medium">
                  {c.label}
                </span>
                <ChevronDown
                  size={17}
                  className={`shrink-0 text-sage-dark transition-transform duration-300 ${
                    openIdx === i ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openIdx === i && (
                <p
                  data-testid={`sauna-consider-body-${i}`}
                  className="text-[16px] leading-[1.65] text-[#454545] pb-5 pr-8 max-w-3xl"
                >
                  {c.note}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
